<template>
  <TWModal
    :show="true"
    :title="logoType === 'primary' ? $t('branding.primaryLogo') : $t('branding.secondaryLogo')"
    :loading="loading"
    @close="handleClose"
  >
    <template slot="content">
      <h4>
        {{
          logoType === 'primary'
            ? $t('branding.uploadLogoHelp')
            : $t('branding.uploadSecondaryLogoHelp')
        }}
      </h4>
      <p>{{ $t('branding.logoRequirements') }}</p>

      <TWFilePicker
        @add="uploadLogo"
        accept="image/jpg, image/jpeg, image/png"
        :uploaderMessage="uploaderMessageText"
        data-ec="branding_logo_modal"
        data-et="file_picker"
        data-hj-suppress
      />

      <div
        v-if="logoFile && !logoValid"
        class="flex justify-center items-center p-base mt-base border border-solid border-danger text-danger rounded-lg"
      >
        <FailIndicator class="mr-base" />
        <span data-test="error">{{ $t('branding.logoInvalid') }}</span>
      </div>

      <h4 class="!font-body mt-base">{{ $t('functional.preview') }}</h4>
      <div
        class="flex justify-center items-center w-48 aspect-16/9 my-base rounded-lg border border-solid border-border"
        :style="{ 'background-color': backgroundColour }"
      >
        <div
          class="w-full h-full logo-bg"
          :style="`background: url('${logo || currentLogo}')`"
        />
      </div>
    </template>

    <template slot="controls">
      <BaseButton
        @click.native="handleClose"
        variant="invisible"
        data-ec="branding_logo_modal"
        data-et="cancel_button"
      >
        {{ $t('functional.cancel') }}
      </BaseButton>

      <BaseButton
        :disabled="!logoValid"
        @click.native="handleSave"
        data-ec="branding_logo_modal"
        data-et="save_button"
      >
        {{ $t('functional.save') }}
      </BaseButton>
    </template>
  </TWModal>
</template>

<script setup>
// @ts-check
import { computed, ref } from 'vue';

import i18n from '@/locales';

import BaseButton from '@/components/library/buttons/BaseButton.vue';
import FailIndicator from '@/components/library/indicators/FailIndicator.vue';
import TWFilePicker from '@/components/tailwind/FilePicker.vue';
import TWModal from '@/components/tailwind/Modal.vue';

const props = defineProps({
  /** @type {import('vue').PropOptions<'primary' | 'secondary'>} */
  logoType: {
    required: false,
    default: 'primary',
  },
  backgroundColour: {
    type: String,
    required: true,
  },
  currentLogo: {
    type: String,
    required: false,
  },
  /** @type {import('vue').PropOptions<(update: File|null) => Promise<void>>} */
  update: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(['close']);

const loading = ref(false);
const logo = ref(null);
const logoFile = ref(null);
const logoValid = ref(false);

const uploaderMessageText = computed(() =>
  // eslint-disable-next-line no-restricted-globals
  (screen.width < 500
    ? i18n.t('functional.uploaderMessageMobile')
    : i18n.t('functional.uploaderMessage')));

async function uploadLogo([file]) {
  logoFile.value = file;

  const reader = new FileReader();
  reader.onload = (e) => {
    logo.value = e.target.result;
  };
  reader.readAsDataURL(file);

  const bmp = await createImageBitmap(file);
  logoValid.value = bmp.width <= 600 && bmp.height <= 600;
  bmp.close();
}

function handleClose() {
  emit('close');
}

async function handleSave() {
  loading.value = true;

  try {
    await props.update(logoFile.value);
    emit('close');
  } finally {
    loading.value = false;
  }
}
</script>
